// Storage Abstraction Layer
// Uses localStorage in browser, can be extended for other storage backends

export interface StorageAdapter {
  get<T>(key: string): Promise<T | null>
  set<T>(key: string, value: T): Promise<void>
  remove(key: string): Promise<void>
  clear(): Promise<void>
}

class LocalStorageAdapter implements StorageAdapter {
  private prefix = 'abcpay:'

  async get<T>(key: string): Promise<T | null> {
    try {
      const item = localStorage.getItem(this.prefix + key)
      return item ? JSON.parse(item) : null
    } catch {
      console.warn(`Failed to get storage key: ${key}`)
      return null
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      localStorage.setItem(this.prefix + key, JSON.stringify(value))
    } catch (error) {
      console.error(`Failed to set storage key: ${key}`, error)
      throw error
    }
  }

  async remove(key: string): Promise<void> {
    localStorage.removeItem(this.prefix + key)
  }

  async clear(): Promise<void> {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(this.prefix)) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key))
  }
}

// Singleton storage instance
export const storage = new LocalStorageAdapter()

// ===== Auth Session Storage =====

export interface AuthSession {
  token: string
  user: {
    id: string
    email: string
    name?: string
  }
  branches: Array<{
    id: string
    name: string
    isActive: boolean
  }>
  selectedBranchId?: string
  environment: string
  apiBaseUrl: string
  expiresAt?: number
}

const AUTH_SESSION_KEY = 'auth-session'

export async function loadAuthSession(): Promise<AuthSession | null> {
  return storage.get<AuthSession>(AUTH_SESSION_KEY)
}

export async function saveAuthSession(session: AuthSession): Promise<void> {
  await storage.set(AUTH_SESSION_KEY, session)
}

export async function clearAuthSession(): Promise<void> {
  await storage.remove(AUTH_SESSION_KEY)
}

// ===== Printer Settings Storage =====

import type { PrinterConfig } from '../types/receipt'
import { DEFAULT_PRINTER_CONFIG } from '@abcpay/shared'

const PRINTER_SETTINGS_KEY = 'printer-settings'

export async function loadPrinterSettings(): Promise<PrinterConfig> {
  const settings = await storage.get<PrinterConfig>(PRINTER_SETTINGS_KEY)
  return settings || DEFAULT_PRINTER_CONFIG
}

export async function savePrinterSettings(config: PrinterConfig): Promise<void> {
  await storage.set(PRINTER_SETTINGS_KEY, config)
}

// ===== Counter Settings Storage =====

import type { BillCounterConfig } from '@abcpay/shared'
import { DEFAULT_COUNTER_CONFIG } from '@abcpay/shared'

const COUNTER_SETTINGS_KEY = 'counter-settings'

export async function loadCounterSettings(): Promise<BillCounterConfig> {
  const settings = await storage.get<BillCounterConfig>(COUNTER_SETTINGS_KEY)
  return settings || DEFAULT_COUNTER_CONFIG
}

export async function saveCounterSettings(config: BillCounterConfig): Promise<void> {
  await storage.set(COUNTER_SETTINGS_KEY, config)
}
